import { AccessReportProviderSetModel } from "@app/provider-sets";
import { ServiceBase } from "@app/shared";
import { ProviderSet, ProviderSetVersionModel, ProviderSetSimpleListModel, ProviderDetailSimpleModel, ProviderSetVersionSummaryCountModel, ProviderSetVersionOverallSummaryModel, ProviderSetHistoryModel, UpdateProviderSetModel, ProviderSetVersionStatusDetailsModel, ProviderSetUploadModel, ProviderSetModelWithActiveProjects } from "./providerSet";
import { ProviderSetsRequestType, ProviderSetVersionSummaryType, ProviderSetDataSortColumn } from "./enumerations";
import { SortDirections } from "../shared/waffles";
import { authenticatedDownloadAsync } from "../shared/AuthenticatedDownload";
import { AddFilterModel, FilterCriteriaModel } from "../shared/FilterCriteria";
import CreateResourceModel from "@app/resources/createResourceModel";
import CreateFilteredResourceModel from "@app/resources/createFilteredResourceModel";
import { ResourceType } from "../resources";
import { querystringify } from "@app/shared/utility";

/** Manages access to CRUD functionality for provider sets. */
export default class ProviderSetService extends ServiceBase {
  /** Retrieves all non-market provider sets accessible to the current user. */
  public async getProviderSets(includeShared?: boolean): Promise<ProviderSet[]> {
    const resources = await this.makeGet<ProviderSet[]>(`providers/provider-sets?includeShared=${!!includeShared}&includeMarket=false&includeVersions=false`);

    // we'll let the caller filter for its use case
    return resources;
  }

  /** Retrieves all market provider sets accessible to the current user. */
  public async getMarketProviderSets(includeShared?: boolean): Promise<ProviderSet[]> {
    const resources = await this.makeGet<ProviderSet[]>(`providers/market-provider-sets?includeShared=${!!includeShared}&includeVersions=false`);

    return resources;
  }

  public async getProviderSetVersions(providerSetId: number): Promise<ProviderSetVersionModel[]> {
    const versions = await this.makeGet<ProviderSetVersionModel[]>(`providers/provider-sets/${providerSetId}/versions`);

    return versions;
  }

  /**
   * Gets the latest version for multiple provider sets.
   * @param clientId The client id of provider set owner.
   * @param providerSetIds The provider set identifiers.
   */
  public async getLatestProviderSetVersion(providerSetIds: number[], clientId: number): Promise<ProviderSetVersionModel[]> {
    const versions: ProviderSetVersionModel[] = await this.makePost<ProviderSetVersionModel[]>(`providers/clients/${clientId}/provider-sets/access-report-latest-version`, providerSetIds);

    return versions;
  }

  public async getAccessReportingProviderSets(): Promise<AccessReportProviderSetModel[]> {
    const providerSets: AccessReportProviderSetModel[] = await this.makeGet<AccessReportProviderSetModel[]>(`providers/access-reporting-provider-sets`);

    return providerSets;
  }

  public async getProviderSetsByRequestType(requestType: ProviderSetsRequestType, skip: number, take: number, sortDirection: SortDirections = SortDirections.None, sortedColumn?: string, nameFilter?: string, descriptionFilter?: string, typeFilter?: string, statusFilter?: string, networkTypeFilter: ResourceType[] = []): Promise<ProviderSetSimpleListModel> {
    const query = {
      descriptionFilter,
      nameFilter,
      networkTypeFilter,
      offset: skip,
      pageSize: take,
      sortDirection,
      sortedColumn,
      statusFilter,
      typeFilter
    };

    const providerSets: ProviderSetSimpleListModel = await this.makeGet<ProviderSetSimpleListModel>(`providers/provider-sets/by-type/${requestType}?${querystringify(query)}`);

    return providerSets;
  }

  public async getProviderSet(providerSetId: string, includeVersions = false, includeFiltered = false, includeFilteredProviderSetVersions = false): Promise<ProviderSet> {
    const query = {
      includeFiltered,
      includeFilteredProviderSetVersions,
      includeVersions,
    };

    const providerSet = await this.makeGet<ProviderSet>(`providers/provider-sets/${providerSetId}?${querystringify(query)}`);

    return providerSet;
  }

  public async getProviderSetVersionDetail(providerSetId: string, providerSetVersionId: string, summaryType: ProviderSetVersionSummaryType, filter?: string, offset?: number, pageSize?: number, orderBy?: ProviderSetDataSortColumn, sortDirection?: SortDirections): Promise<ProviderDetailSimpleModel[]> {
    const query = {
      filter,
      offset,
      orderBy: !orderBy ? ProviderSetDataSortColumn.NPI : orderBy,
      pageSize,
      sortDirection: !sortDirection ? SortDirections.None : sortDirection,
    };

    const providerDetails: ProviderDetailSimpleModel[] = await this.makeGet<ProviderDetailSimpleModel[]>(`providers/provider-sets/${providerSetId}/versions/${providerSetVersionId}/summary/${summaryType}/provider-data?${querystringify(query)}`);

    return providerDetails;
  }

  public async getProviderSetVersionOverallSummary(providerSetId: string, providerSetVersionId: string): Promise<ProviderSetVersionOverallSummaryModel> {
    const overallSummary = await this.makeGet<ProviderSetVersionOverallSummaryModel>(`providers/provider-sets/${providerSetId}/versions/${providerSetVersionId}/overall-summary`);

    return overallSummary;
  }

  public async getProviderSetVersionSummaryCount(providerSetId: string, providerSetVersionId: string, summaryType: ProviderSetVersionSummaryType): Promise<ProviderSetVersionSummaryCountModel> {
    const summary = await this.makeGet<ProviderSetVersionSummaryCountModel>(`providers/provider-sets/${providerSetId}/versions/${providerSetVersionId}/summary/${summaryType}`);

    return summary;
  }

  public async getProviderSetHistory(providerSetId: string): Promise<ProviderSetHistoryModel> {
    const history = await this.makeGet<ProviderSetHistoryModel>(`providers/provider-sets/${providerSetId}/history`);

    return history;
  }

  public async getFilterProviderSetColumnNames(providerSetId: string): Promise<string[]> {
    const columnNames = await this.makeGet<string[]>(`providers/provider-sets/${providerSetId}/filter-columns`);

    return columnNames;
  }

  public async providerSetUpdateFilter(providerSetId: number, providerSetVersionId: number, criteria: FilterCriteriaModel[], filterInvalidRecords: boolean): Promise<void> {
    const updateModel: AddFilterModel = {
      Criteria: criteria,
      FilterInvalidRecords:  filterInvalidRecords
    }
    await this.makePost<void>(`/providers/provider-sets/${providerSetId}/versions/${providerSetVersionId}/update-filter`, updateModel);
  }

  public async addProviderSetVersion(providerSetId: number, criteria?: FilterCriteriaModel[], filterInvalidRecords?: boolean ): Promise<ProviderSetVersionModel> {
    const newVersion = await this.makePost<ProviderSetVersionModel>(`/providers/provider-sets/${providerSetId}/versions`);

    if (!!criteria) {
      await this.providerSetUpdateFilter(providerSetId, newVersion.providerSetVersionId, criteria, filterInvalidRecords);
    }

    return newVersion;
  }

  public async archiveProviderSet(providerSetId: string): Promise<void> {
    await this.makePost<void>(`/providers/provider-sets/${providerSetId}/archive`);
  }

  public async unarchiveProviderSet(providerSetId: string): Promise<void> {
    await this.makePost<void>(`/providers/provider-sets/${providerSetId}/unarchive`);
  }

  public async purgeProviderSet(providerSetId: string): Promise<void> {
    await this.makePost<void>(`/providers/provider-sets/${providerSetId}/purge`);
  }

  public async activateProviderSet(providerSetId: string, providerSetVersionId: string, filteredProviderSetIds: string[]): Promise<void> {
    await this.makePost<void>(`/providers/provider-sets/${providerSetId}/versions/${providerSetVersionId}/activate`, filteredProviderSetIds);
  }

  public async addProviderSet(providerSet: CreateResourceModel): Promise<ProviderSet> {
    return await this.makePost<ProviderSet>(`/providers/provider-sets/`, providerSet);
  }

  public async addFilteredProviderSet(filteredProviderSet: CreateFilteredResourceModel): Promise<ProviderSet> {
    return await this.makePost<ProviderSet>(`/providers/provider-sets/filtered/add`, filteredProviderSet);
  }

  public async updateProviderSet(providerSetId: number, providerSetVersionId: number, updateModel: UpdateProviderSetModel): Promise<void> {
    await this.makePost<void>(`/providers/provider-sets/${providerSetId}/versions/${providerSetVersionId}/update`, updateModel);
  }

  public async getProviderSetVersionStatus(providerSetId: string, providerSetVersionId: string): Promise<ProviderSetVersionStatusDetailsModel> {
    return await this.makeGet<ProviderSetVersionStatusDetailsModel>(`/providers/provider-sets/${providerSetId}/versions/${providerSetVersionId}/status`);
  }

  public async createProviderSetVersionIndexesAsync(providerSetId: string, providerSetVersionId: string): Promise<void> {
    await this.makePost<void>(`/providers/provider-sets/${providerSetId}/versions/${providerSetVersionId}/create-index`);
  }

  public async getProviderSetNetworkTaxonomyReport(fileName?: string, clientId?: number): Promise<void> {
    let urlSearchParams = new URLSearchParams();

    if (!!clientId) {
      const query = {
        clientId: clientId.toString()
      };
      urlSearchParams = new URLSearchParams(query)
    }

    if (!!fileName && fileName.split('.').pop().toLowerCase() !== `csv`) {
      fileName += `.csv`;
    }

    return await authenticatedDownloadAsync(`${window.apiEndpointV2_0}/providers/provider-sets/report/csv`, null, fileName, urlSearchParams);
  }

  public async getProviderSetVersionUploadModels(providerSetId: number, versionId: number): Promise<ProviderSetUploadModel[]> {
    return await this.makeGet(`/providers/provider-sets/${providerSetId}/versions/${versionId}/uploads`);
  }

  public async getProviderSetNPIDetailsSummaryJobs(providerSetId: string, versionId: string): Promise<boolean> {
   return await this.makeGet<boolean>(`/providers/provider-sets/${providerSetId}/version/${versionId}/npi-details-jobs`);
  }

  public async createProviderSetNPIDetailsSummary(providerSetId: string, versionId: string): Promise<void> {
    await this.makePost<void>(`/providers/provider-sets/${providerSetId}/version/${versionId}/create-npi-details-summary`);
  }

  public async GetEditTemplateOptionsProviderSetData(includeArchived: boolean, includeOnlyClientAssigned: boolean, includeVersions: boolean, universalOnly: boolean): Promise<ProviderSetModelWithActiveProjects[]>{
    const query = {
      includeArchived,
      includeOnlyClientAssigned,
      includeVersions,
      universalOnly
    };
    return await this.makeGet<ProviderSetModelWithActiveProjects[]>(`/providers/GetEditTemplateOptionsProviderSetData?${querystringify(query)}`);
  }
}
